import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { differentials } from '@/mocks/homeData';

export default function DifferentialsSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  const lineRef = useRef<HTMLDivElement>(null);

  return (
    <section ref={ref} className="relative bg-background-100 py-24 md:py-32 lg:py-40 overflow-hidden">
      {/* Decorative organic shape */}
      <div className="absolute top-0 left-0 right-0 h-16 bg-background-50 organic-divider" />

      {/* Subtle background */}
      <div className="absolute inset-0 opacity-[0.012] bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary-400 via-transparent to-transparent" />

      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        {/* Header */}
        <div className="text-center max-w-[650px] mx-auto mb-20 md:mb-28">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-xs tracking-[0.2em] uppercase text-primary-400 font-label"
          >
            Por que a Femme Vie
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl text-foreground-950 mt-4 leading-[1.1]"
          >
            O que nos torna<br />
            <span className="italic font-light">diferentes.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.18 }}
            className="text-sm text-foreground-600 leading-relaxed mt-5 max-w-md mx-auto"
          >
            Cada detalhe da sua experiência foi pensado para transmitir cuidado, confiança e exclusividade.
          </motion.p>
        </div>

        {/* Vertical Timeline — Animated */}
        <div className="relative max-w-[900px] mx-auto">
          {/* Center line — animated reveal */}
          <div ref={lineRef} className="absolute left-6 md:left-1/2 top-0 bottom-0 w-[1px] md:-translate-x-px">
            <motion.div
              initial={{ scaleY: 0 }}
              animate={isInView ? { scaleY: 1 } : {}}
              transition={{ duration: 1.5, ease: [0.25, 0.1, 0.25, 1] }}
              className="h-full w-full bg-gradient-to-b from-primary-300/50 via-primary-300/30 to-transparent origin-top"
            />
          </div>

          {differentials.map((item, index) => {
            const isEven = index % 2 === 0;

            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 40 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.7, delay: 0.25 + 0.12 * index, ease: [0.25, 0.1, 0.25, 1] }}
                className={`relative flex items-start gap-6 md:gap-0 pb-16 last:pb-0 ${
                  isEven ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                {/* Timeline dot — pulsing */}
                <div className="absolute left-6 md:left-1/2 top-0 -translate-x-1/2 z-10">
                  <span className="relative flex h-[13px] w-[13px]">
                    <motion.span
                      animate={{ scale: [1, 1.6, 1], opacity: [1, 0, 1] }}
                      transition={{ repeat: Infinity, duration: 3, delay: index * 0.4, ease: 'easeInOut' }}
                      className="absolute inset-0 rounded-full bg-primary-300/50"
                    />
                    <span className="relative inline-flex rounded-full h-[13px] w-[13px] bg-background-100 border-2 border-primary-400 shadow-[0_0_0_4px_var(--background-100)]" />
                  </span>
                </div>

                {/* Content Card */}
                <div className={`ml-14 md:ml-0 md:w-[calc(50%-40px)] ${isEven ? 'md:pr-8 md:text-right' : 'md:pl-8'}`}>
                  {/* Number */}
                  <span className={`block font-heading text-5xl md:text-6xl italic font-light text-foreground-100 leading-none mb-3 select-none ${
                    isEven ? 'md:text-right' : ''
                  }`}>
                    {String(index + 1).padStart(2, '0')}
                  </span>

                  {/* Icon + Title */}
                  <div className={`flex items-center gap-4 mb-3 ${isEven ? 'md:flex-row-reverse' : ''}`}>
                    <motion.div
                      whileHover={{ scale: 1.1, backgroundColor: 'var(--primary-100)' }}
                      className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 flex-shrink-0 transition-colors duration-300"
                    >
                      <i className={`${item.icon} text-xl`}></i>
                    </motion.div>
                    <h3 className="font-heading text-2xl md:text-3xl text-foreground-950 leading-tight">
                      {item.title}
                    </h3>
                  </div>

                  {/* Description */}
                  <p className={`text-sm text-foreground-600 leading-relaxed ${isEven ? 'md:text-right' : ''}`}>
                    {item.description}
                  </p>
                </div>

                {/* Empty spacer for the other side */}
                <div className="hidden md:block md:w-[calc(50%-40px)]" />
              </motion.div>
            );
          })}
        </div>

        {/* Decorative bottom */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="mt-20 flex justify-center"
        >
          <div className="flex items-center gap-3 text-xs tracking-[0.1em] uppercase text-primary-400 font-label">
            <motion.span
              animate={{ scaleX: [1, 1.5, 1] }}
              transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
              className="w-8 h-[1px] bg-primary-300/50 inline-block origin-center"
            />
            Excelência em cada detalhe
            <motion.span
              animate={{ scaleX: [1, 1.5, 1] }}
              transition={{ repeat: Infinity, duration: 3, delay: 1.5, ease: 'easeInOut' }}
              className="w-8 h-[1px] bg-primary-300/50 inline-block origin-center"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}